import React, { useEffect, useState } from 'react';
import PageContent from '@/components/PageContent';
import { Col, Divider, Form, Grid, IconButton, NumberInput, Panel, Row, SelectPicker, Stack, FormInstance, Schema, Message } from 'rsuite';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import ErrorMessage from '@/components/ErrorMessage';
import { AreaTypeStoryChapterTask, CityTypeStoryChapterTask, CountryTypeStoryChapterTask, RunClubTypeStoryChapterTask, SpecificStartEndTypeStoryChapterTask, SpecificType, StoryChapterTask, StoryChapterTaskCalculationTypeEnum, StoryChapterTaskCalculationTypeList, StoryChapterTaskTypeEnum, StoryChapterTaskTypeInfo, TimeTypeStoryChapterTask, UpsertSpecificType, UpsertStoryChapterTask } from '@/models/story-chapter-task.model';
import { upsertStoryChapterTask } from '@/services/story-chapter-task.service';
import CountryTypeTaskForm from './country-story-chapter-task-form';
import CityTypeTaskForm from './city-story-chapter-task-form';
import AreaTypeTaskForm from './area-story-chapter-task-form';
import TimeTypeTaskForm from './time-story-chapter-task-form';
import SpecificStartEndTypeTaskForm from './specific-start-end-story-chapter-task-form';
import RunClubTypeTaskForm from './run-club-story-chapter-task-form';

const modelSchema = Schema.Model<UpsertStoryChapterTask> ({
    id: Schema.Types.NumberType(),
    type: Schema.Types.NumberType().isRequired('Type is required'),
    name: Schema.Types.StringType().isRequired('Name is required'),
    order: Schema.Types.NumberType().isRequired('Order is required'),
    firstLevelAmount: Schema.Types.NumberType().isRequired('1st level amount is required'),
    secondLevelAmount: Schema.Types.NumberType(),
    thirdLevelAmount: Schema.Types.NumberType(),
    chapterId: Schema.Types.NumberType().isRequired('Chapter Id is required'),
    calculationType: Schema.Types.NumberType().isRequired('Chapter Id is required'),
});

const emptyModel: UpsertStoryChapterTask = {
    id: undefined,
    type: StoryChapterTaskTypeEnum.City,
    name: '',
    order: undefined,
    firstLevelAmount: undefined,
    secondLevelAmount: undefined,
    thirdLevelAmount: undefined,
    chapterId: undefined,
    calculationType: StoryChapterTaskCalculationTypeEnum.Sum
};

interface MainStoryChapterTaskFormProps {
    chapterId?: number;
    formObject?: StoryChapterTask;
    specificFormObject?: SpecificType;
    pageState: PageState,
}

const MainStoryChapterTaskForm: React.FC<MainStoryChapterTaskFormProps> = 
  ({chapterId, formObject, specificFormObject, pageState}) => {
    const [formValue, setFormValue] = useState<UpsertStoryChapterTask>(emptyModel);
    const [initSpecificFormObject, setInitSpecificFormObject] = useState<SpecificType|undefined>(undefined);
    const [specificFormValue, setSpecificFormValue] = useState<UpsertSpecificType|undefined>(undefined);
    const [errorModel, setErrorModel] = useState<BaseErrorModel|undefined>(undefined);
    const [loading, setLoading] = useState(0);

    const nav = useNavigate();
    const formRef = React.useRef<FormInstance<Record<string, any>>>(null);
    const specificFormRef = React.useRef<FormInstance<Record<string, any>>>(null);

    useEffect(() => {
        if(pageState == PageState.New){
          setFormValue({
            ...emptyModel, 
            chapterId: chapterId,
          });
          return;
        }
        if (formObject) {
          setFormValue(formObject);
          setSpecificFormValue(specificFormObject);
          setInitSpecificFormObject(specificFormObject);
        } else {
          setFormValue(emptyModel);
          setInitSpecificFormObject(undefined);
          setSpecificFormValue(undefined);
        }
    }, [formObject]);
    
    const handleSubmit = async () => {
        const mainFormCheck = formRef.current && !formRef.current.check(err => console.log(err));
        const specificFormCheck = specificFormRef.current && !specificFormRef.current.check(err => console.log(err));

        if (mainFormCheck || specificFormCheck){
            return;
        }
        setErrorModel(undefined);
        setLoading(pre=>++pre);
        try {
            const result: BaseResponseModel<null> = await upsertStoryChapterTask({...formValue, ...specificFormValue});
            if (result.success) {
                nav('/stories/chapters/' + formValue.chapterId, {state:{tab:'story-chapter-tasks'}});
            }
        } catch (err) {
            setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
        }
        finally{
            setLoading(pre=>--pre);
        }
    };
    
    const handleSpecificFormValueChange = (newFormValue: SpecificType) => {
      setSpecificFormValue(newFormValue);
    };

    const handleFormChange = (value: Record<string, any>) => {
      setFormValue(prevFormValue => ({
        ...prevFormValue,
        ...value
      }));
    };

    const handleTypeChange = type => {
      setFormValue(pre => ({ ...pre, type}));
      setInitSpecificFormObject(undefined);
    };
    
    return (
    <>
      <PageContent>
          <Grid w='100%' fluid>
            <Row>
              <Col span={{xs:24, xl:12}}>
                <Panel bordered>
                  <Form name='upsert-story-chapter-task' fluid 
                    ref={formRef}
                    onChange={handleFormChange}
                    formValue={formValue}
                    model={modelSchema}
                    disabled={!!loading}
                  >
                    <Row>
                      <Col span={{xs:24, lg:12}} >
                        <Form.Group>
                          <Form.Label>Type</Form.Label>
                          <Form.Control name="type" accepter={SelectPicker} block
                            data={Object.values(StoryChapterTaskTypeInfo)} 
                            searchable={false} 
                            onChange={handleTypeChange}
                            cleanable={false}
                            disabled={pageState == PageState.Edit}
                          />
                        </Form.Group>
                      </Col>
                      <Col span={{xs:24, lg:12}}>
                        <Form.Group>
                          <Form.Label>Order</Form.Label>
                          <Form.Control name="order" accepter={NumberInput} />
                        </Form.Group>                    
                      </Col>                      
                    </Row> 
                    <Row>
                      <Col span={{xs:24}}>
                        <Form.Group>
                          <Form.Label>Name</Form.Label>
                          <Form.Control name="name" value={formValue?.name ?? ''}/>
                        </Form.Group>
                      </Col>
                    </Row>              
                    <Row>
                      <Col span={{xs:24, lg:12}} >
                        <Form.Group>
                          <Form.Label>Calculation Type</Form.Label>
                          <Form.Control name="calculationType" accepter={SelectPicker} block
                            data={StoryChapterTaskCalculationTypeList} 
                            searchable={false} 
                            cleanable={false}
                          />
                        </Form.Group>
                      </Col>
                      <Col span={{xs:24, lg:12}} >
                        <Form.Group>
                          <Form.Label>1st Level Amount ({StoryChapterTaskTypeInfo[formValue.type!].amountUnit})</Form.Label>
                          <Form.Control name="firstLevelAmount" accepter={NumberInput}
                            onChange={val => setFormValue(pre => ({...pre, firstLevelAmount: val ? val : undefined}))}
                          />
                        </Form.Group>
                      </Col>
                      <Col span={{xs:24, lg:12}}>
                        <Form.Group>
                          <Form.Label>2nd Level Amount ({StoryChapterTaskTypeInfo[formValue.type!].amountUnit})</Form.Label>
                          <Form.Control name="secondLevelAmount" accepter={NumberInput} 
                            onChange={val => setFormValue(pre => ({...pre, secondLevelAmount: val ? val : undefined}))}
                          />
                        </Form.Group>                    
                      </Col>
                      <Col span={{xs:24, lg:12}}>
                        <Form.Group>
                          <Form.Label>3rd Level Amount ({StoryChapterTaskTypeInfo[formValue.type!].amountUnit})</Form.Label>
                          <Form.Control name="thirdLevelAmount" accepter={NumberInput} 
                            onChange={val => setFormValue(pre => ({...pre, thirdLevelAmount: val ? val : undefined}))}                          
                          />
                        </Form.Group>                    
                      </Col>
                    </Row>
                  </Form> 
                </Panel>
              </Col>
              <Col span={{xs:24, xl:12}}>
                <Panel bordered>
                  {(formValue.type == StoryChapterTaskTypeEnum.Distance || formValue.type == StoryChapterTaskTypeEnum.Elevation) &&
                    <Message showIcon type="info"> There is no specific field. </Message>
                  }
                  {formValue.type == StoryChapterTaskTypeEnum.Area && 
                    <AreaTypeTaskForm 
                        formObject={initSpecificFormObject as AreaTypeStoryChapterTask}  
                        onFormChange={handleSpecificFormValueChange}  
                        formRef={specificFormRef}
                        loading={loading}
                    />
                  }
                  {formValue.type == StoryChapterTaskTypeEnum.Country && 
                    <CountryTypeTaskForm 
                        formObject={initSpecificFormObject as CountryTypeStoryChapterTask}  
                        onFormChange={handleSpecificFormValueChange}  
                        formRef={specificFormRef}
                        loading={loading}
                    />
                  }
                  {formValue.type == StoryChapterTaskTypeEnum.City && 
                    <CityTypeTaskForm 
                        formObject={initSpecificFormObject as CityTypeStoryChapterTask}  
                        onFormChange={handleSpecificFormValueChange}  
                        formRef={specificFormRef}
                        loading={loading}
                    />
                  }
                  {formValue.type == StoryChapterTaskTypeEnum.Time && 
                    <TimeTypeTaskForm 
                        formObject={initSpecificFormObject as TimeTypeStoryChapterTask}  
                        onFormChange={handleSpecificFormValueChange}  
                        formRef={specificFormRef}
                        loading={loading}
                    />
                  }
                  {formValue.type == StoryChapterTaskTypeEnum.SpecificStartEnd && 
                    <SpecificStartEndTypeTaskForm 
                        formObject={initSpecificFormObject as SpecificStartEndTypeStoryChapterTask}  
                        onFormChange={handleSpecificFormValueChange}  
                        formRef={specificFormRef}
                        loading={loading}
                    />
                  }
                  {formValue.type == StoryChapterTaskTypeEnum.RunClub && 
                    <RunClubTypeTaskForm 
                        formObject={initSpecificFormObject as RunClubTypeStoryChapterTask}  
                        onFormChange={handleSpecificFormValueChange}  
                        formRef={specificFormRef}
                        loading={loading}
                    />
                  }
                </Panel>
              </Col>
            </Row>
          </Grid>

        <Divider />
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

        <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
            Back
        </IconButton>
        <IconButton
            icon={<PageNextIcon />}
            placement="right"
            appearance="primary"
            onClick={handleSubmit}
            loading={!!loading}
        >
            Save
        </IconButton>
        </Stack>

      </PageContent>
    </>
    );
};


export default MainStoryChapterTaskForm;